import { io, Socket } from 'socket.io-client'

/**
 * Generic socket service for a single Socket.io namespace.
 *
 * Best-practice design:
 *  - on(event, cb) returns an unsubscribe function -- no listener leaks.
 *  - All application events are forwarded via onAny; lifecycle events
 *    (connect / disconnect / connect_error) are forwarded via the same
 *    listener map so callers never touch the raw Socket object.
 *  - emit() is a no-op (with a warning) when not connected.
 */
class NamespacedSocket {
  private socket: Socket | null = null
  private readonly listeners: Map<string, Set<(data: unknown) => void>> = new Map()
  private readonly namespace: string
  private readonly apiUrl: string = (() => {
    const raw = import.meta.env.VITE_API_URL || ''
    return raw.replace(/\/+$/g, '') // strip trailing slashes
  })()

  constructor(namespace: string) {
    this.namespace = namespace
  }

  connect(): void {
    if (this.socket?.connected) return

    if (this.socket) {
      this.socket.connect()
      return
    }

    const target = this.apiUrl ? `${this.apiUrl}/${this.namespace}` : `/${this.namespace}`
    this.socket = io(target, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    })

    this.socket.on('connect', () => {
      console.log(`[${this.namespace}] Connected:`, this.socket?.id)
      this.forward('connect', undefined)
    })

    this.socket.on('disconnect', () => {
      console.log(`[${this.namespace}] Disconnected`)
      this.forward('disconnect', undefined)
    })

    this.socket.on('connect_error', (err: Error) => {
      console.error(`[${this.namespace}] Connection error:`, err)
      this.forward('connect_error', err)
    })

    // Forward all application events through the listener map.
    // Lifecycle events are handled above; skip them here to avoid double calls.
    this.socket.onAny((event: string, data: unknown) => {
      if (event === 'connect' || event === 'disconnect' || event === 'connect_error') return
      this.forward(event, data)
    })
  }

  disconnect(): void {
    this.socket?.disconnect()
    this.socket = null
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false
  }

  /** Returns the current socket ID, or null if not connected. */
  getId(): string | null {
    return this.socket?.id ?? null
  }

  emit(event: string, data?: unknown): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data)
    } else {
      console.warn(`[${this.namespace}] Cannot emit '${event}': not connected`)
    }
  }

  /** Subscribe to an event. Returns an unsubscribe function. */
  on<T = unknown>(event: string, callback: (data: T) => void): () => void {
    // Wrap the user callback so internal storage remains `unknown` typed
    const wrapper = (d: unknown) => callback(d as T)
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(wrapper)
    return () => this.listeners.get(event)?.delete(wrapper)
  }

  private forward(event: string, data: unknown): void {
    this.listeners.get(event)?.forEach(cb => cb(data))
  }
}

export const dispatchSocket = new NamespacedSocket('dispatch')
export const accountingSocket = new NamespacedSocket('accounting')
export const stockSocket = new NamespacedSocket('stock')
export const carStorageSocket = new NamespacedSocket('car-storage')
export const invoicingSocket = new NamespacedSocket('invoicing')

// Backward-compat alias used by legacy imports.
export { dispatchSocket as socketService }
